import { useState } from 'react';
import { UpsellItem } from '@guest-widgets/shared/src/types/product/upsellItem';
import { CartItem } from '@guest-widgets/shared/src/types/product/cartItem';
import { TrackingEventType } from '@guest-widgets/shared/src/utils/customerTracking/types';
import { handleTrackingEvent } from '@guest-widgets/shared/src/utils/customerTracking/utils/trackingEventsHandler';

import { useCart } from '../../contexts/cartContext/cartContext';
import { useUpsell } from '../../contexts/upsellContext/upsellContext';

type Trigger = 'backward' | 'back-history';

export const useWarningDialogHandler = () => {
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [trigger, setTrigger] = useState<Trigger>('backward');

  const { upsells, setUpsells } = useUpsell();
  const { removeItem, cartWithSteps } = useCart();

  const hasRequiredUpsellWithZeroQuantity = (upsells: UpsellItem[]) => {
    return upsells.some(
      (upsell) =>
        upsell.configuration.isRequired && (!upsell.totalQuantity || upsell.totalQuantity === 0)
    );
  };

  const isWarningNeeded = hasRequiredUpsellWithZeroQuantity(upsells);

  const openWarningDialog = (trigger: Trigger) => {
    if (hasRequiredUpsellWithZeroQuantity(upsells)) {
      setIsWarningOpen(true);
      setTrigger(trigger);
    }
  };

  const closeWarningDialog = () => {
    setIsWarningOpen(false);
  };

  const removeParentFromCart = async (item?: CartItem): Promise<void> => {
    if (!item) return;

    await removeItem.mutateAsync(item);
    closeWarningDialog();
    setUpsells([]);

    const { totals } = cartWithSteps?.data?.cart ?? {};

    if (!totals) return;

    handleTrackingEvent(TrackingEventType.REMOVE_FROM_CART, {
      item,
      currency: totals.subtotal.gross.currencyCode,
      totalAmount: totals.subtotal.gross.amount,
    });
  };

  return {
    isWarningOpen,
    isWarningNeeded,
    openWarningDialog,
    closeWarningDialog,
    removeParentFromCart,
    trigger,
  };
};
